import React from 'react';
import { useLang, PRIVACY_POLICY } from '@shootout-comps/i18n';

interface Props {
  onBack?: () => void;
}

// Renders trusted, app-authored HTML (bold/link/code markup baked into the translated content
// in libs/i18n/src/privacyPolicy.ts) — never user input, so this carries no XSS risk.
function Html({ html, style }: { html: string; style?: React.CSSProperties }) {
  return <span style={style} dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function PrivacyPolicyPage({ onBack }: Props) {
  const { t, lang } = useLang();
  const p = PRIVACY_POLICY[lang];

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--baize-dark)', color: 'var(--chalk)', fontFamily: 'inherit' }}>
      {/* Nav bar */}
      <div style={{ borderBottom: '1px solid rgba(201,168,76,0.2)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', position: 'sticky', top: 0, background: 'var(--baize-dark)', zIndex: 10 }}>
        {onBack && (
          <button onClick={onBack} style={{ background: 'none', border: '1px solid rgba(201,168,76,0.3)', borderRadius: '6px', color: 'var(--gold)', cursor: 'pointer', padding: '0.35rem 0.75rem', fontSize: '0.8rem' }}>
            ← {t.backBtn}
          </button>
        )}
        <span style={{ fontFamily: 'Bebas Neue', fontSize: '1.4rem', color: 'var(--gold)', letterSpacing: '0.05em' }}>{t.privacyPolicy}</span>
      </div>

      <div className="privacy-content" style={{ maxWidth: '760px', margin: '0 auto', padding: '2.5rem 1.5rem 4rem' }}>

        <p style={meta}>{p.lastUpdated}</p>

        <p style={body}><Html html={p.intro} /></p>

        <h2 style={h2}>{p.s1.heading}</h2>
        <p style={body}><Html html={p.s1.body} /></p>

        <h2 style={h2}>{p.s2.heading}</h2>
        {p.s2.subs.map((sub, i) => (
          <React.Fragment key={i}>
            <h3 style={h3}>{sub.heading}</h3>
            <p style={body}><Html html={sub.body} /></p>
          </React.Fragment>
        ))}

        <h2 style={h2}>{p.s3.heading}</h2>
        <p style={body}>{p.s3.intro}</p>
        <ul style={ul}>
          {p.s3.items.map((item, i) => <li key={i} style={li}><Html html={item} /></li>)}
        </ul>

        <h2 style={h2}>{p.s4.heading}</h2>
        <p style={body}><Html html={p.s4.body} /></p>

        <h2 style={h2}>{p.s5.heading}</h2>
        <p style={body}><Html html={p.s5.body} /></p>

        <h2 style={h2}>{p.s6.heading}</h2>
        <p style={body}>{p.s6.intro}</p>
        <ul style={ul}>
          {p.s6.items.map((item, i) => <li key={i} style={li}><Html html={item} /></li>)}
        </ul>
        <p style={body}>{p.s6.outro}</p>

        <h2 style={h2}>{p.s7.heading}</h2>
        <p style={body}>{p.s7.intro}</p>
        <ul style={ul}>
          {p.s7.items.map((item, i) => <li key={i} style={li}><Html html={item} /></li>)}
        </ul>
        <p style={body}><Html html={p.s7.outro} /></p>

        <h2 style={h2}>{p.s8.heading}</h2>
        <p style={body}><Html html={p.s8.body} /></p>

        <h2 style={h2}>{p.s9.heading}</h2>
        <p style={body}><Html html={p.s9.body} /></p>

        <h2 style={h2}>{p.s10.heading}</h2>
        <p style={body}><Html html={p.s10.body} /></p>

        <h2 style={h2}>{p.s11.heading}</h2>
        <p style={body}><Html html={p.s11.body} /></p>

      </div>

      <style>{`
        .privacy-content a { color: var(--gold); text-decoration: underline; }
        .privacy-content code { background: rgba(255,255,255,0.08); border-radius: 3px; padding: 0.1em 0.35em; font-size: 0.85em; color: var(--chalk); }
      `}</style>
    </div>
  );
}

const h2: React.CSSProperties = {
  fontFamily: 'Bebas Neue', fontSize: '1.25rem', color: 'var(--gold)',
  letterSpacing: '0.04em', margin: '2rem 0 0.6rem', borderBottom: '1px solid rgba(201,168,76,0.2)',
  paddingBottom: '0.4rem',
};
const h3: React.CSSProperties = {
  fontSize: '0.95rem', color: 'var(--chalk)', fontWeight: 600, margin: '1.2rem 0 0.4rem',
};
const body: React.CSSProperties = {
  color: 'var(--chalk-dim)', fontSize: '0.9rem', lineHeight: 1.75, margin: '0 0 0.9rem',
};
const meta: React.CSSProperties = {
  color: 'var(--chalk-dim)', fontSize: '0.78rem', margin: '0 0 1.5rem', fontStyle: 'italic',
};
const ul: React.CSSProperties = {
  paddingLeft: '1.4rem', margin: '0 0 0.9rem',
};
const li: React.CSSProperties = {
  color: 'var(--chalk-dim)', fontSize: '0.9rem', lineHeight: 1.75, marginBottom: '0.4rem',
};
